<template>
  <b-card>
    <b-alert v-if="err" show variant="danger">
      <p><pre>{{ err }}</pre></p>
    </b-alert>
    <template v-else-if="!company">
      <no-ssr><loading-spinner class="d-flex justify-content-center my-5"/></no-ssr>
    </template>
    <template v-else>
      <h3>Emails</h3>
      <p>Enable/disable various types of emails the platform sends out to users</p>
      <p v-if="isSuperAdmin || (isDemoSite && isRecruiter)">To send emails manually visit our <b-link @click="$router.push({ name: 'EmailSender' })">email sender tool</b-link></p>

      <form-block
        v-for="(block, key) in blocks"
        v-show="!(block.hide && typeof block.hide === 'function' && block.hide($root))"
        :key="key"
        :id="key"
        class="py-3"
        :company="company"
        :title="block.title"
        :description="block.description"
        :test-email="block.sendTestEmail"
        :err="formErrs[key]"
        :state="formStates[key]"
        v-on:updateState="($event) => $set(formStates, key, $event)"
        :tabs="block.tabs || null"
        :mock-data="block.mockData"
        :test-data-query="block.testDataQuery"
        :fields="block.fields"
        :value="getValues(key)"
        @input="values => saveValues(key, values)"/>
    </template>
  </b-card>
</template>

<script>
import _get from 'lodash/get';
import gql from 'graphql-tag';
import { mapRoot } from '@/mixins/root';
import FormBlock from '@/components/settings/FormBlock';
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import emailBlocks from '@/emails/previews';

const BLOCKS = {
  customiseEmailTypes: {
    title: 'Email Behaviours',
    description: 'Enable/disable various behaviours of emails the platform sends',
    fields: [
      // {
      //   key: 'preferences.sendReferAgainRequestEmails',
      //   label: 'Allow referral requests to be sent to an employee more than once',
      //   description: 'Send referral requests to employees that have not actioned the requests previously',
      //   type: 'boolean',
      // },
      // {
      //   key: 'preferences.sendReferAgainReminderEmails',
      //   label: 'Send referral request reminders to employees',
      //   description: 'Send reminders to employees who haven\'t actioned referral requests',
      //   type: 'boolean',
      // },
      // {
      //   key: 'preferences.sendNotYetSyncedContactsReminderEmails',
      //   label: 'Remind employees to sync their contacts',
      //   description: 'Send reminders to employees who haven\'t synced their contacts',
      //   type: 'boolean',
      // },
      // {
      //   key: 'preferences.sendStatsEmails',
      //   label: 'Send Stats emails reminders to Recruiters',
      //   description: 'Send weekly stats emails to recruiters',
      //   type: 'boolean',
      // },
      {
        key: 'preferences.sendApplicationCreatedEmailsToRecruiters',
        label: 'Recruiters New Application Email Notifications',
        description: 'Notify recruiters by email when a new application is created that they own',
        type: 'boolean',
      },
      {
        key: 'preferences.sendApplicationCreatedEmailsToEmployees',
        label: 'Employee New Application Email Notifications',
        description: 'Notify employees by email when a new application is created using their referral codes',
        type: 'boolean',
      },
      {
        key: 'preferences.sendApplicationUpdatedEmailsToEmployees',
        label: 'Employee Updated Application Email Notifications',
        description: 'Notify employees by email when an application of theirs is updated',
        type: 'boolean',
      },
      {
        key: 'emails.name',
        label: 'From Name',
        description: 'Name of the email sender.',
        type: 'string',
      },
      {
        key: 'preferences.emailHideBrandLogo',
        label: 'Hide brand logo in emails',
        description: 'If you prefer to only use the Email Header Image, you can hide the brand logo from emails',
        type: 'boolean',
      },
    ],
  },
  ...emailBlocks,
};

const CompanyEmailSettingsFragment = gql`
  fragment CompanyEmailSettings on Company {
    id

    name
    branding {
      name
    }

    crons(where: { function: "emails-$STAGE-bulkController" }) {
      id
      name
      function
      payload
      status
    }

    emails {
      name
    }

    preferences {
      emailHideBrandLogo
      sendApplicationCreatedEmailsToRecruiters
      sendApplicationCreatedEmailsToEmployees
      sendApplicationUpdatedEmailsToEmployees
      sendWeeklyEmployeeNewJobsEmails
      sendReferAgainRequestEmails
      sendReferAgainReminderEmails
      sendNotYetSyncedContactsReminderEmails
      sendStatsEmails
      employeeWelcomeEmailEmailPlain
      employeeWeeklyJobsEmailPlain
      employeeReengageHowItWorksEmailPlain
      makeFirstReferralEmailPlain
      employeeReengageSyncNetworksFirstEmailPlain
      employeeReengageSyncNetworksSecondEmailPlain
      employeeReengageSyncNetworksThirdEmailPlain
      inviteEmailFirstPlain
      inviteEmailSecondPlain
      inviteEmailThirdPlain
      employeeReengageYourRewardsAreWaitingEmailPlain
      employeeReengageNoReferralsNoWorriesEmailPlain
      employeeReengageYouAreProEmailPlain
      employeeReengageLatestMatchesEmailPlain
      employeeReengageKeepIntroducingEmailPlain
      employeeReengageLinkedInReadyFirstEmailPlain
      employeeReengageLinkedInReadySecondEmailPlain
      imMentorApprovedEmailPlain
      imMentorNewRequestEmailPlain
      imMentorRejectedEmailPlain
      imMentorRequestApprovedEmailPlain
      imMentorRequestRejectedEmailPlain
      imMentorStatusChangedEmailPlain
      imNewJobApplicationEmployeeEmailPlain
      imNewJobApplicationRecruiterEmailPlain
      imEmployeeWelcomeEmailPlain
      imInviteEmailFirstPlain
      imInviteEmailSecondPlain
      imInviteEmailThirdPlain
      # imIsYourMentorshipActiveEmailPlain
      # imIsYourMentorshipStillActiveEmailPlain
      inviteMentorEmailFirstPlain
      inviteMentorEmailSecondPlain
      inviteMentorEmailThirdPlain
    }

    templates(where: {
      keys: [
        "EMAIL_EMPLOYEE_WEEKLY_JOBS_SUBJECT",
        "EMAIL_EMPLOYEE_WEEKLY_JOBS_BODY_ONE",
        "EMAIL_EMPLOYEE_WEEKLY_JOBS_PLAIN_HTML",
        "EMAIL_EMPLOYEE_REENGAGE_HOWITWORKS_SUBJECT",
        "EMAIL_EMPLOYEE_REENGAGE_HOWITWORKS_BODY_ONE",
        "EMAIL_EMPLOYEE_REENGAGE_HOWITWORKS_BODY_TWO",
        "EMAIL_EMPLOYEE_REENGAGE_HOWITWORKS_PLAIN_HTML",
        "EMAIL_EMPLOYEE_REENGAGE_MAKEFIRSTREFERRAL_SUBJECT",
        "EMAIL_EMPLOYEE_REENGAGE_MAKEFIRSTREFERRAL_BODY_ONE",
        "EMAIL_EMPLOYEE_REENGAGE_MAKEFIRSTREFERRAL_BODY_TWO",
        "EMAIL_EMPLOYEE_REENGAGE_MAKEFIRSTREFERRAL_PLAIN_HTML",
        "EMAIL_EMPLOYEE_REENGAGE_SYNCNETWORKS_FIRST_SUBJECT",
        "EMAIL_EMPLOYEE_REENGAGE_SYNCNETWORKS_FIRST_BODY_ONE",
        "EMAIL_EMPLOYEE_REENGAGE_SYNCNETWORKS_FIRST_BODY_TWO",
        "EMAIL_EMPLOYEE_REENGAGE_SYNCNETWORKS_FIRST_PLAIN_HTML",
        "EMAIL_EMPLOYEE_REENGAGE_SYNCNETWORKS_SECOND_SUBJECT",
        "EMAIL_EMPLOYEE_REENGAGE_SYNCNETWORKS_SECOND_BODY_ONE",
        "EMAIL_EMPLOYEE_REENGAGE_SYNCNETWORKS_SECOND_BODY_TWO",
        "EMAIL_EMPLOYEE_REENGAGE_SYNCNETWORKS_SECOND_PLAIN_HTML",
        "EMAIL_EMPLOYEE_REENGAGE_SYNCNETWORKS_THIRD_SUBJECT",
        "EMAIL_EMPLOYEE_REENGAGE_SYNCNETWORKS_THIRD_BODY_ONE",
        "EMAIL_EMPLOYEE_REENGAGE_SYNCNETWORKS_THIRD_BODY_TWO",
        "EMAIL_EMPLOYEE_REENGAGE_SYNCNETWORKS_THIRD_PLAIN_HTML",
        "EMAIL_INVITE_FIRST_SUBJECT",
        "EMAIL_INVITE_FIRST_BODY_ONE",
        "EMAIL_INVITE_FIRST_PLAIN_HTML",
        "EMAIL_INVITE_SECOND_SUBJECT",
        "EMAIL_INVITE_SECOND_BODY_ONE",
        "EMAIL_INVITE_SECOND_PLAIN_HTML",
        "EMAIL_INVITE_THIRD_SUBJECT",
        "EMAIL_INVITE_THIRD_BODY_ONE",
        "EMAIL_INVITE_THIRD_PLAIN_HTML",
        "EMAIL_EMPLOYEE_WELCOME_EMAIL_SUBJECT",
        "EMAIL_EMPLOYEE_WELCOME_EMAIL_BODY_ONE",
        "EMAIL_EMPLOYEE_WELCOME_EMAIL_BODY_TWO",
        "EMAIL_EMPLOYEE_WELCOME_EMAIL_BODY_THREE",
        "EMAIL_EMPLOYEE_WELCOME_EMAIL_PLAIN_HTML",
        "EMAIL_EMPLOYEE_REENGAGE_REWARDS_ARE_WAITING_SUBJECT",
        "EMAIL_EMPLOYEE_REENGAGE_REWARDS_ARE_WAITING_BODY_ONE",
        "EMAIL_EMPLOYEE_REENGAGE_REWARDS_ARE_WAITING_PLAIN_HTML",
        "EMAIL_EMPLOYEE_NO_REFERRALS_NO_WORRIES_EMAIL_SUBJECT",
        "EMAIL_EMPLOYEE_NO_REFERRALS_NO_WORRIES_EMAIL_BODY_ONE",
        "EMAIL_EMPLOYEE_NO_REFERRALS_NO_WORRIES_EMAIL_PLAIN_HTML",
        "EMAIL_EMPLOYEE_NEW_CONTENT_TO_SHARE_EMAIL_SUBJECT",
        "EMAIL_EMPLOYEE_NEW_CONTENT_TO_SHARE_EMAIL_BODY_ONE",
        "EMAIL_EMPLOYEE_NEW_CONTENT_TO_SHARE_EMAIL_PLAIN_HTML",
        "EMAIL_EMPLOYEE_PLATFORM_PRO_SUBJECT",
        "EMAIL_EMPLOYEE_PLATFORM_PRO_BODY_ONE",
        "EMAIL_EMPLOYEE_PLATFORM_PRO_PLAIN_HTML",
        "EMAIL_EMPLOYEE_LATEST_MATCHES_SUBJECT",
        "EMAIL_EMPLOYEE_LATEST_MATCHES_BODY_ONE",
        "EMAIL_EMPLOYEE_LATEST_MATCHES_PLAIN_HTML",
        "EMAIL_EMPLOYEE_KEEP_INTRODUCING_SUBJECT",
        "EMAIL_EMPLOYEE_KEEP_INTRODUCING_BODY_ONE",
        "EMAIL_EMPLOYEE_KEEP_INTRODUCING_PLAIN_HTML",
        "EMAIL_EMPLOYEE_LINKEDIN_REMINDER_FIRST_EMAIL_SUBJECT",
        "EMAIL_EMPLOYEE_LINKEDIN_REMINDER_FIRST_EMAIL_BODY_ONE",
        "EMAIL_EMPLOYEE_LINKEDIN_REMINDER_FIRST_EMAIL_PLAIN_HTML",
        "EMAIL_EMPLOYEE_LINKEDIN_REMINDER_SECOND_EMAIL_SUBJECT",
        "EMAIL_EMPLOYEE_LINKEDIN_REMINDER_SECOND_EMAIL_BODY_ONE",
        "EMAIL_EMPLOYEE_LINKEDIN_REMINDER_SECOND_EMAIL_PLAIN_HTML",
        "EMAIL_CLAIMED_REWARD_STATUS_CHANGED_BODY_ONE",
        "EMAIL_IM_MENTOR_APPROVED_SUBJECT",
        "EMAIL_IM_MENTOR_APPROVED_BODY_ONE",
        "EMAIL_IM_MENTOR_APPROVED_PLAIN_HTML",
        "EMAIL_IM_MENTOR_NEW_REQUEST_SUBJECT",
        "EMAIL_IM_MENTOR_NEW_REQUEST_PLAIN_HTML",
        "EMAIL_IM_MENTOR_NEW_REQUEST_BODY_ONE",
        "EMAIL_IM_MENTOR_REJECTED_SUBJECT",
        "EMAIL_IM_MENTOR_REJECTED_PLAIN_HTML",
        "EMAIL_IM_MENTOR_REJECTED_BODY_ONE",
        "EMAIL_IM_MENTOR_REQUEST_APPROVED_SUBJECT",
        "EMAIL_IM_MENTOR_REQUEST_APPROVED_PLAIN_HTML",
        "EMAIL_IM_MENTOR_REQUEST_APPROVED_BODY_ONE",
        "EMAIL_IM_MENTOR_REQUEST_REJECTED_SUBJECT",
        "EMAIL_IM_MENTOR_REQUEST_REJECTED_PLAIN_HTML",
        "EMAIL_IM_MENTOR_REQUEST_REJECTED_BODY_ONE",
        "EMAIL_IM_MENTOR_STATUS_CHANGED_SUBJECT",
        "EMAIL_IM_MENTOR_STATUS_CHANGED_PLAIN_HTML",
        "EMAIL_IM_NEW_JOB_APPLICATION_EMPLOYEE_SUBJECT",
        "EMAIL_IM_NEW_JOB_APPLICATION_EMPLOYEE_PLAIN_HTML",
        "EMAIL_IM_NEW_JOB_APPLICATION_EMPLOYEE_BODY_ONE",
        "EMAIL_IM_NEW_JOB_APPLICATION_RECRUITER_SUBJECT",
        "EMAIL_IM_NEW_JOB_APPLICATION_RECRUITER_PLAIN_HTML",
        "EMAIL_IM_NEW_JOB_APPLICATION_RECRUITER_BODY_ONE",
        "EMAIL_IM_JOB_APPLICATION_REJECTED_SUBJECT",
        "EMAIL_IM_JOB_APPLICATION_REJECTED_BODY_ONE",
        "EMAIL_IM_JOB_APPLICATION_REJECTED_PLAIN_HTML",
        "EMAIL_INVITE_MENTOR_ONE_SUBJECT",
        "EMAIL_INVITE_MENTOR_ONE_BODY_ONE",
        "EMAIL_INVITE_MENTOR_ONE_PLAIN_HTML"
        "EMAIL_INVITE_MENTOR_TWO_SUBJECT",
        "EMAIL_INVITE_MENTOR_TWO_BODY_ONE",
        "EMAIL_INVITE_MENTOR_TWO_PLAIN_HTML"
        "EMAIL_INVITE_MENTOR_THREE_SUBJECT",
        "EMAIL_INVITE_MENTOR_THREE_BODY_ONE",
        "EMAIL_INVITE_MENTOR_THREE_PLAIN_HTML",
        "EMAIL_IM_EMPLOYEE_WELCOME_EMAIL_SUBJECT",
        "EMAIL_IM_EMPLOYEE_WELCOME_EMAIL_PLAIN_HTML",
        "EMAIL_IM_EMPLOYEE_WELCOME_EMAIL_BODY_ONE",
        "EMAIL_IM_INVITE_FIRST_SUBJECT",
        "EMAIL_IM_INVITE_FIRST_BODY_ONE",
        "EMAIL_IM_INVITE_FIRST_PLAIN_HTML",
        "EMAIL_IM_IS_YOUR_MENTORSHIP_STILL_ACTIVE_SUBJECT",
        "EMAIL_IM_IS_YOUR_MENTORSHIP_STILL_ACTIVE_PLAIN_HTML",
        "EMAIL_IM_IS_YOUR_MENTORSHIP_STILL_ACTIVE_BODY_ONE",
        "EMAIL_IM_IS_YOUR_MENTORSHIP_ACTIVE_SUBJECT",
        "EMAIL_IM_IS_YOUR_MENTORSHIP_ACTIVE_PLAIN_HTML",
        "EMAIL_IM_IS_YOUR_MENTORSHIP_ACTIVE_BODY_ONE"
      ]
    }) {
      key
      lang
      type
      default
      placeholder
      value
    }
  }
`;

const SettingsQuery = gql`
  query SettingsEmails {
    company {
      ...CompanyEmailSettings
    }
  }

  ${CompanyEmailSettingsFragment}
`;

const SettingsMutation = gql`
  mutation SettingsEmails($update: CompanyUpdateInput!) {
    updateCompany(update: $update) {
      ...CompanyEmailSettings
    }
  }

  ${CompanyEmailSettingsFragment}
`;

export default {
  name: 'CompanyEmailSettings',
  components: {
    FormBlock,
    LoadingSpinner,
  },
  data: () => ({
    company: null,
    err: null,
    formErrs: Object.keys(BLOCKS).reduce((states, key) => {
      states[key] = null;
      return states;
    }, {}),
    formStates: Object.keys(BLOCKS).reduce((states, key) => {
      states[key] = null;
      return states;
    }, {}),
  }),
  metaInfo: {
    title: 'Email Settings',
  },
  apollo: {
    company: {
      query: SettingsQuery,
      error(err) { this.err = `${err}`; },
    },
  },
  computed: {
    ...mapRoot([ 'isRecruiter', 'isSuperAdmin', 'isRecruiterAdmin', 'isDemoSite' ]),
    blocks() {
      const { isInternalMobilityEnabled } = this.$root;
      return Object.keys(BLOCKS).reduce((out, key) => {
        const { isInternalMobility } = BLOCKS[key];
        if (!isInternalMobility || isInternalMobilityEnabled === isInternalMobility) {
          out[key] = BLOCKS[key];
        }
        return out;
      }, {});
    },
  },
  methods: {
    getValues(key) {
      return this.blocks[key].fields.reduce((values, field) => {
        if (field.type === 'template') {
          values[field.key] = this.company.templates.find(t => field.key.replace('template.', '') === t.key) || {};
          const companyName = this.company.branding.name || this.company.name;
          values[field.key].placeholder = values[field.key].placeholder ? values[field.key].placeholder.replace('[Platform Name]', companyName) : values[field.key].placeholder;
          values[field.key].value = values[field.key].value ? values[field.key].value.replace('[Platform Name]', companyName) : values[field.key].value;
        } else {
          values[field.key] = _get(this.company, field.key, null);
        }
        return values;
      }, {});
    },
    async saveValues(key, values) {
      try {
        this.$log.debug(values);

        const block = this.blocks[key];
        if (!block) {
          throw new Error(`Unknown block ${key}`);
        }

        this.formStates[key] = 'SAVING';
        this.formErrs[key] = null;

        const update = block.fields.reduce((update, field) => {
          let value = values[field.key];
          if (field.type.indexOf('asset-') === 0 && value && value.id) {
            value = { id: value.id };
          }

          if (field.key.indexOf('branding.') === 0) {
            update.branding = update.branding || {};
            update.branding[field.key.replace('branding.', '')] = value;
          } else if (field.key.indexOf('preferences.') === 0) {
            update.preferences = update.preferences || {};
            update.preferences[field.key.replace('preferences.', '')] = value;
          } else if (field.key.indexOf('emails.') === 0) {
            update.emails = update.emails || {};
            update.emails[field.key.replace('emails.', '')] = value;
          } else if (field.key.indexOf('template.') === 0) {
            const { key, lang, value: v } = value;
            update.templates = update.templates || [];
            update.templates.push({ key, lang, value: v });
            console.log('TEMPLATES: ', update.templates);
          } else {
            update[field.key] = value;
          }
          return update;
        }, {});

        this.$log.info(update);
        await this.$apollo.mutate({
          mutation: SettingsMutation,
          variables: { update },
          update(store, { data: { updateCompany: company } }) {
            const data = store.readQuery({ query: SettingsQuery });
            data.company = company;
            store.writeQuery({ query: SettingsQuery, data });
          },
        });

        this.formStates[key] = 'SAVED';
      } catch (err) {
        console.error(err);

        this.formStates[key] = 'ERRORED';
        this.formErrs[key] = `${err}`;
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/settings/Emails.vue